import React, {memo, forwardRef} from 'react';
import PropTypes from 'prop-types';
import {usePopoverContext} from './utils';
import {useCombinedRefs} from 'utils/react';

/**
 * PopoverTrigger - Component that triggers the popover to open/close
 * Wraps any element and makes it clickable to toggle the popover
 *
 * @typedef {Object} PopoverTriggerProps
 * @property {React.ReactNode} children - The trigger element (e.g., Button)
 * @property {boolean} [asChild] - If true, clone the child element instead of wrapping
 */
const PopoverTrigger = memo(
	forwardRef(({children, asChild = false, ...props}, forwardedRef) => {
		const {referenceRef, handleToggle, open} = usePopoverContext();

		const {style, onClick, ...restProps} = props;

		// Always call hooks at the top level, not conditionally
		const mergedRef = useCombinedRefs(
			asChild && React.isValidElement(children) ? children.ref : null,
			referenceRef,
			forwardedRef,
		);

		const handleClick = e => {
			onClick?.(e);
			handleToggle();
		};

		if (asChild && React.isValidElement(children)) {
			// Clone the child and add our props
			// If the child can't accept a ref (function component without forwardRef),
			// React will warn, but we still need to pass it for popper positioning
			// The parent component should use forwardRef if it needs to work with refs
			return React.cloneElement(children, {
				ref: mergedRef,
				onClick: e => {
					children.props.onClick?.(e);
					handleClick(e);
				},
				'aria-expanded': open,
				'aria-haspopup': 'menu',
				...restProps,
			});
		}

		return (
			<div
				ref={mergedRef}
				aria-expanded={open}
				aria-haspopup="menu"
				onClick={handleClick}
				// Default wrapper is inline-block so popper placement="top|bottom|left|right"
				// centers relative to the actual trigger content (not a full-width block).
				style={{display: 'inline-block', ...style}}
				{...restProps}
			>
				{children}
			</div>
		);
	}),
);

PopoverTrigger.propTypes = {
	children: PropTypes.node.isRequired,
	asChild: PropTypes.bool,
};

PopoverTrigger.displayName = 'PopoverTrigger';

export default PopoverTrigger;
